import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import toast from "react-hot-toast";
import { IThunkRejectValue, RootState } from "../../../types";
import { getExtractErrors } from "../../../apis";
import { CustomErrorToast } from "../../../components/general/Toast";
import { getAiJobApi, getAiJobLogsApi } from "../../../apis/ai-jobsAPI";
import { AiJobState, AiJob, AiJobLog } from "../../../types/ai";

const initialState: AiJobState = {
  job: null,
  jobLoading: false,

  logs: [],
  logsLoading: false,
};

export const getAiJobAsync = createAsyncThunk<
  { job: AiJob },
  { id: string; withoutLoading?: boolean },
  IThunkRejectValue
>(
  "job",
  async (
    { id, withoutLoading = false },
    { rejectWithValue, fulfillWithValue, dispatch, requestId }
  ) => {
    try {
      dispatch(
        getAiJobAsync.pending(requestId, {
          id,
          withoutLoading,
        })
      );
      const response = await getAiJobApi(id);
      const job = response.data.Result;
      return fulfillWithValue({ job });
    } catch (e) {
      return rejectWithValue({ message: getExtractErrors(e) });
    }
  }
);

export const getAiJobLogsAsync = createAsyncThunk<
  { logs: AiJobLog[] },
  { id: string; withoutLoading?: boolean },
  IThunkRejectValue
>(
  "job-logs",
  async (
    { id, withoutLoading = false },
    { rejectWithValue, fulfillWithValue, dispatch, requestId }
  ) => {
    try {
      dispatch(
        getAiJobLogsAsync.pending(requestId, {
          id,
          withoutLoading,
        })
      );
      const response = await getAiJobLogsApi(id);
      const logs = response.data.Result.logs;
      return fulfillWithValue({ logs });
    } catch (e) {
      return rejectWithValue({ message: getExtractErrors(e) });
    }
  }
);

export const aiJobSlice = createSlice({
  name: "job",
  initialState,
  reducers: {
    handleClearAiJobSlice: (state) => {
      state.job = initialState.job;
      state.jobLoading = initialState.jobLoading;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAiJobAsync.pending, (state, action) => {
        if (!action.meta.arg.withoutLoading) state.jobLoading = true;
      })
      .addCase(getAiJobAsync.fulfilled, (state, action) => {
        const { job } = action.payload;
        state.job = job;
        state.jobLoading = false;
      })
      .addCase(getAiJobAsync.rejected, (state, { payload }) => {
        state.jobLoading = false;
        if (payload?.message)
          toast.error(() => CustomErrorToast(payload?.message));
      });

    builder
      .addCase(getAiJobLogsAsync.pending, (state, action) => {
        if (!action.meta.arg.withoutLoading) state.logsLoading = true;
      })
      .addCase(getAiJobLogsAsync.fulfilled, (state, action) => {
        const { logs } = action.payload;
        state.logs = logs;
        state.logsLoading = false;
      })
      .addCase(getAiJobLogsAsync.rejected, (state, { payload }) => {
        state.logsLoading = false;
        if (payload?.message)
          toast.error(() => CustomErrorToast(payload?.message));
      });
  },
});

export const selectAiJob = (state: RootState) => state.aiJob.job;
export const selectAiJobLoading = (state: RootState) => state.aiJob.jobLoading;

export const selectAiJobLogs = (state: RootState) => state.aiJob.logs;
export const selectAiJobLogsLoading = (state: RootState) =>
  state.aiJob.logsLoading;

export const { handleClearAiJobSlice } = aiJobSlice.actions;
export default aiJobSlice.reducer;
